import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button, Modal } from "capitalrx-components-2.0/lib/general";
import { Paragraph, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Stack } from "capitalrx-components-2.0/lib/layout";

import { AccumulationValues, Accumulators, DisplayNetworkStatuses, DisplayScopes } from "@/App/types/accumulation";
import { formatDollars } from "@/App/utils/utils";


export interface AccumulatorModalProps {
  readonly balances: AccumulationValues;
  readonly accumulatorType: Accumulators;
  readonly displayScope: DisplayScopes;
  // we will use this eventually i suppose
  readonly displayNetworkStatus?: DisplayNetworkStatuses;
  readonly isVisible: boolean;
  readonly onClose: () => void;
}

export const AccumulatorModal: FC<AccumulatorModalProps> = ({ balances, accumulatorType, isVisible, onClose }) => {
  const { t: translate } = useTranslation();

  const accumulatorLabels: Record<Accumulators, string> = {
    deductible: translate("ACCUMULATOR_MODAL.deductible"),
    outOfPocket: translate("ACCUMULATOR_MODAL.outOfPocket"),
    planPay: translate("ACCUMULATOR_MODAL.planPay"),
    planPayFertility: translate("ACCUMULATOR_MODAL.fertilityMaximum"),
  };

  const maxAmount = useMemo(() => {
    return formatDollars(balances?.max?.toNumber());
  }, [balances?.max]);

  return (
    <Modal testId={`accumulator-description-${accumulatorType}`}
      visible={isVisible}
      title={accumulatorLabels[accumulatorType]}
      onClose={onClose}
      footer={(
        <Stack alignment="right">
          <Button onClick={onClose}>{translate("ACCUMULATOR_MODAL.okay")}</Button>
        </Stack>
      )}
    >
      {accumulatorType === Accumulators.Deductible && (
        <>
          <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypeDeductible.balance")} <Semibold>{maxAmount}</Semibold></Paragraph>
          <Paragraph testId={`accumulator-content-${accumulatorType}`}>{translate("ACCUMULATOR_MODAL.accumulatorTypeDeductible.accumulatorContent")}</Paragraph>
          <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypeDeductible.accumulatorNote")}</Paragraph>
        </>
      )}
      {accumulatorType === Accumulators.OutOfPocket && (
        <>
          <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypeOutOfPocket.balance")} <Semibold>{maxAmount}</Semibold></Paragraph>
          <Paragraph testId={`accumulator-content-${accumulatorType}`}>{translate("ACCUMULATOR_MODAL.accumulatorTypeOutOfPocket.accumulatorContent")}</Paragraph>
          <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypeOutOfPocket.accumulatorNote")}</Paragraph>
        </>
      )}
      {accumulatorType === Accumulators.PlanPay && (
        <>
          {maxAmount !== formatDollars("0") && <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypePlanPay.balance")} <Semibold>{maxAmount}</Semibold></Paragraph>}
          <Paragraph testId={`accumulator-content-${accumulatorType}`}>{translate("ACCUMULATOR_MODAL.accumulatorTypePlanPay.accumulatorContent")}</Paragraph>
        </>
      )}
      {accumulatorType === Accumulators.PlanPayFertility && (
        <>
          {maxAmount !== formatDollars("0") && <Paragraph>{translate("ACCUMULATOR_MODAL.accumulatorTypePlanPayFertility.fertilityMaximum")} <Semibold>{maxAmount}</Semibold></Paragraph>}
          <Paragraph testId={`accumulator-content-${accumulatorType}`}>{translate("ACCUMULATOR_MODAL.accumulatorTypePlanPayFertility.accumulatorContent")}</Paragraph>
        </>
      )}
    </Modal>
  );
};
